import { createFileRoute } from "@tanstack/react-router"
import { useState, useRef, useEffect } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { TextField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-chat-link-generator")!

export const Route = createFileRoute("/tools/instagram-chat-link-generator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-chat-link-generator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-chat-link-generator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-chat-link-generator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-chat-link-generator" },
      ]}),
      createHowToSchema({ name: "How to Generate an Instagram Chat Link", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: ChatLinkGeneratorPage,
})

function generateChatLink(username: string): string {
  const clean = username.trim().replace(/^@/, "")
  return `https://ig.me/m/${clean}`
}

function ChatLinkGeneratorPage() {
  const [username, setUsername] = useState("")
  const [chatLink, setChatLink] = useState("")
  const [copied, setCopied] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const generate = () => {
    const clean = username.trim().replace(/^@/, "")
    if (!clean) return
    setChatLink(generateChatLink(clean))
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(chatLink)
      setCopied(true)
    } catch {
      const input = document.createElement("input")
      input.value = chatLink
      document.body.appendChild(input)
      input.select()
      document.execCommand("copy")
      document.body.removeChild(input)
      setCopied(true)
    }
  }

  const reset = () => {
    setUsername("")
    setChatLink("")
    setCopied(false)
  }

  const cleanUsername = username.trim().replace(/^@/, "")

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>What is an Instagram Chat Link?</SectionHeading>
            <Paragraph className="mb-4">
              An Instagram chat link is a direct URL that uses the <code className="text-brand-button">ig.me/m/</code> format to open a DM conversation with your Instagram account. It is Instagram's official deep-linking format for direct messages. When someone taps or clicks a link like <code className="text-brand-button">https://ig.me/m/yourusername</code>, they are taken straight to a DM conversation with you — no searching for your username, no navigating through the app, no extra steps whatsoever.
            </Paragraph>
            <Paragraph className="mb-4">
              The link works across every platform and device. On mobile, it opens the Instagram app directly to your conversation thread. On desktop, it opens Instagram's web interface with your DM chat ready to go. If the person does not have the Instagram app installed, the link gracefully falls back to the mobile web version, so you never lose a potential conversation.
            </Paragraph>
            <Paragraph className="mb-4">
              Unlike sharing your profile URL (which takes people to your feed and hopes they find the message button), a chat link is purpose-built for conversation. The person who clicks it has one clear path: send you a message. This makes it the ideal link to use anywhere you want to start a dialogue — whether that is capturing leads, answering customer questions, or building relationships with your audience.
            </Paragraph>
            <Paragraph>
              Chat links are completely free to generate and use. Instagram does not limit how many you create or how often they can be clicked. They do not expire, and they work for both personal and business accounts. If you ever change your username, you simply generate a new link — the format stays the same, only the username portion changes.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>Where to Use Your Chat Link</SectionHeading>
            <FeatureGrid items={[
              { icon: "📱", title: "Instagram Bio", description: "Your Instagram bio is the single most visited page on your profile. Adding your chat link here gives every visitor a one-tap way to start a conversation. Replace vague CTAs like \"DM for collabs\" with a clickable link that removes all guesswork." },
              { icon: "💼", title: "Business Cards and Packaging", description: "Print your chat link on business cards, product packaging, or thank-you inserts. Customers who receive a physical product can scan or type the link to reach you instantly. It bridges the gap between your physical brand and your digital inbox." },
              { icon: "📧", title: "Email Signatures", description: "Add your chat link to your email signature so every email you send doubles as a conversation starter. Recipients who prefer Instagram over email can reach you on their favorite platform with a single click." },
              { icon: "🌐", title: "Website Contact Pages", description: "Embed a \"Message us on Instagram\" button on your website's contact page. Many customers — especially younger demographics — prefer messaging over filling out contact forms. A chat link gives them a familiar, low-friction option." },
              { icon: "📷", title: "QR Codes at Events", description: "Convert your chat link into a QR code and display it at trade shows, pop-up shops, or networking events. Attendees can scan the code with their phone camera and land directly in your DMs — no typing, no searching, no app-switching." },
              { icon: "📦", title: "Product Packaging", description: "Include your chat link on product labels or inserts to create a direct support channel. Customers with questions about sizing, ingredients, or usage can message you instantly instead of searching for your support email." },
              { icon: "📄", title: "Printed Marketing Materials", description: "Flyers, brochures, posters, and print ads can all include your chat link. It turns static marketing into an interactive touchpoint. Pair the link with a clear call to action like \"Message us for 10% off\" to drive immediate conversations." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Why Chat Links Beat "DM Me" CTAs</SectionHeading>
            <Paragraph className="mb-4">
              Telling someone to "DM me" sounds simple, but it actually requires multiple steps: the person needs to open Instagram, search for your exact username, navigate to your profile, find the message button, and then type a message. Each step in that chain is a point where people drop off. Research on user behavior consistently shows that every additional click or tap in a funnel reduces conversion rates by 20% or more. A five-step process can lose the majority of interested people before they ever send a single word.
            </Paragraph>
            <Paragraph className="mb-4">
              A chat link collapses all of that into a single tap. One click and the DM thread is open, ready for them to type. That friction reduction is not marginal — it is transformational. Businesses that switch from "DM me" CTAs to direct chat links consistently report two to three times more inbound conversations.
            </Paragraph>
            <Paragraph className="mb-4">
              Chat links also work from anywhere, not just Instagram. You can place them on your website, in emails, on Twitter, in YouTube descriptions, or in a TikTok bio. "DM me on Instagram" only works if the person is already on Instagram. A chat link works from any browser on any device.
            </Paragraph>
            <Paragraph>
              Beyond convenience, chat links project a professional image. They signal that you have invested in making it easy for people to reach you. You can also track clicks by adding UTM parameters to the landing pages where you share the link, giving you data on which channels drive the most conversations.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>Combining Chat Links with DM Automation</SectionHeading>
            <Paragraph className="mb-4">
              A chat link on its own gets people into your inbox. But what happens when they arrive? If you are asleep, in a meeting, or simply overwhelmed with messages, that lead goes cold. This is where <a href="/tools/instagram-dm-roi-calculator" className="text-brand-button hover:underline">DM automation</a> changes the equation entirely.
            </Paragraph>
            <Paragraph className="mb-4">
              When someone clicks your chat link and sends a message, Zapify can automatically greet them with a personalized welcome message, deliver a lead magnet (like a free guide, discount code, or resource link), or start an interactive conversation flow that qualifies leads before you ever get involved. The person gets an instant response — no waiting — and you get a pre-qualified lead in your inbox.
            </Paragraph>
            <Paragraph>
              This combination turns a simple link into an automated lead capture system. Share your chat link in a reel caption ("DM me 'GUIDE' to get the free playbook"), and Zapify detects the keyword and delivers the resource automatically. You build your audience, capture emails, and nurture leads around the clock — even while you sleep. The chat link is the entry point; automation is the engine that converts attention into action.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>Tips for Maximizing Chat Link Conversions</SectionHeading>
            <TipList tips={[
              { title: "Add context about what happens when they DM", description: "Do not just share a bare link. Tell people what to expect: \"Tap to get your free pricing guide\" or \"Message us for a personalized recommendation.\" When people know what they will receive, they are far more likely to click." },
              { title: "Use action-oriented language", description: "Swap generic phrases like \"Contact us\" for specific CTAs: \"Tap to get your free guide,\" \"Message us for 20% off,\" or \"DM 'START' to begin.\" Action words create urgency and clarity, which drive higher click-through rates." },
              { title: "Test the link yourself before sharing", description: "Click your chat link on both mobile and desktop to verify it opens correctly. Try it from a different Instagram account to see the experience from your audience's perspective. A broken link destroys trust instantly." },
              { title: "Pair the link with a clear offer", description: "Chat links convert best when they are attached to something valuable. A free resource, an exclusive discount, early access to a product, or a free consultation gives people a reason to tap. The link is the mechanism; the offer is the motivation." },
              { title: "Track clicks with UTM parameters on your landing pages", description: "While you cannot add UTM parameters to the ig.me link itself, you can track which landing pages or channels send the most people to your chat link. Use UTM-tagged pages where you embed the link, then check your analytics to see which sources drive the most DM conversations." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!chatLink ? (
            <div className="space-y-4">
              <TextField
                label="Instagram Username"
                value={username}
                onChange={setUsername}
                placeholder="@yourusername"
                required
              />
              <p className="text-xs text-neutral-400 dark:text-neutral-500 -mt-2">
                Enter your username with or without the @ symbol
              </p>
              <SubmitButton onClick={generate} disabled={!cleanUsername}>
                Generate Chat Link
              </SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Chat link display */}
              <div>
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">Your Instagram Chat Link</p>
                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-4">
                  <p className="text-sm sm:text-base text-neutral-800 dark:text-neutral-200 break-all font-mono">{chatLink}</p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={copyToClipboard}
                  className={`flex-1 h-12 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    copied
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  }`}
                >
                  {copied ? "Copied!" : "Copy Link"}
                </button>
                <a
                  href={chatLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 h-12 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all flex items-center justify-center"
                >
                  Open in Instagram
                </a>
              </div>

              {/* Shareable card */}
              <div ref={cardRef} className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-gradient-to-br from-brand-accent/5 via-brand-accent/5 to-brand-accent/5 dark:from-brand-accent/10 dark:via-brand-accent/10 dark:to-brand-accent/10 p-6 sm:p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-accent flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <p className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white mb-1">DM me on Instagram</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">@{chatLink.split("/").pop()}</p>
                <div className="inline-block rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 px-4 py-2">
                  <p className="text-xs font-mono text-neutral-600 dark:text-neutral-300 break-all">{chatLink}</p>
                </div>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-3">Tap the link or scan to start a conversation</p>
              </div>

              {/* Generate another */}
              <ResetButton onClick={reset}>Generate Another</ResetButton>
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
